<template>
  <div
    class="c-content u-mbm"
    :class="{ 'is-truncated': isOverflown && isTruncated }"
  >
    <!--
      Readme content is sanitized by python-repository-interface
      https://github.com/Ircam-Wam/python-repository-interface/blob/36ac7e07cac1ca882fee062b16956c9ddff0ccc1/repository/vendors/utils.py#L92-L104
    -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div ref="el" class="c-content__wrapper" v-html="readmeCmsContent" />

    <template v-if="isOverflown">
      <button
        v-if="isTruncated" class="c-content__toggle"
        @click.prevent="toggleContent">
        <span class="c-content__label">{{ t('readMore') }}</span>
        <svg class="c-content__icon" viewBox="0 0 24 24">
          <path d="m19.659 12.248c-.417-.363-1.047-.321-1.411.094l-5.248 5.997v-15.339c0-.552-.448-1-1-1s-1 .448-1 1v15.339l-5.248-5.997c-.364-.416-.996-.458-1.411-.094-.416.364-.458.995-.094 1.411l7 8c.007.008.018.011.025.019.067.072.149.125.235.174.032.019.057.047.091.061.124.055.259.087.402.087s.278-.032.401-.087c.034-.015.059-.043.091-.061.086-.05.168-.102.235-.174.007-.008.018-.011.025-.019l7-8c.364-.416.322-1.048-.093-1.411z" />
        </svg>
      </button>

      <button v-else class="c-content__toggle" @click.prevent="toggleContent">
        <span class="c-content__label">{{ t('readLess') }}</span>
        <svg class="c-content__icon" viewBox="0 0 24 24">
          <path d="m19.752 10.341-7-8c-.007-.008-.017-.01-.024-.017-.183-.197-.438-.324-.728-.324s-.545.127-.728.324c-.007.008-.018.01-.024.017l-7 8c-.364.416-.322 1.047.094 1.411.417.364 1.048.322 1.411-.094l5.247-5.997v15.339c0 .552.448 1 1 1s1-.448 1-1v-15.339l5.248 5.997c.197.226.474.342.752.342.233 0 .468-.082.658-.248.416-.363.458-.995.094-1.411z" />
        </svg>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from '@/utils/i18n'
import imagesLoaded from 'imagesloaded'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

const props = defineProps<{
  html: string
}>()

const readmeCmsContent = ref(DOMPurify.sanitize(marked.parseInline(props.html), { ADD_TAGS: [ 'iframe' ], ADD_ATTR: [ 'src', 'width', 'height', 'allowfullscreen' ] }))
// console.log('readmeCmsContent: ', readmeCmsContent.value)

const { t } = useI18n({
  messages: {
    fr: {
      readMore: 'Lire plus',
      readLess: 'Lire moins'
    },
    en: {
      readMore: 'Read more',
      readLess: 'Read less'
    }
  }
})

const el = ref<HTMLDivElement>()

const isOverflown = ref(false)
const isTruncated = ref(true)

function toggleContent () {
  isTruncated.value = !isTruncated.value
}

function setIsOverflown () {
  if (!el.value) {
    throw new Error('el is undefined')
  }
  // show the read more / less button if the readme content is
  // taller than 5 times the window height
  isOverflown.value = el.value.scrollHeight > window.innerHeight * 4

  // overwrite style to prevent overflow
  const contentInnerElement = el.value.firstChild as HTMLElement
  if (contentInnerElement) {
    if (contentInnerElement.style) {
      contentInnerElement.style.whiteSpace = 'pre-line'
    }
  }
}

onMounted(() => {
  // console.log('HTML CONTENT: ', props.html)

  watch(() => props.html, () => {
    // watches readme content, update setIsOverflown after images are loaded
    imagesLoaded(el.value!, setIsOverflown)
  }, { immediate: true, flush: 'post' })
})
</script>

<style scoped lang="scss">
@use "sass-rem" as rem;
@use "@/sass/settings";
@use "@/sass/helpers/gradients";
@use "@/sass/helpers/font";

.c-content.is-truncated .c-content__wrapper {
  max-height: 500vh;
  overflow: hidden;
  position: relative;

  &::after {
    background: gradients.scrim(settings.$color-background, "to top");
    content: "";
    display: block;
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: rem.convert(36px);
  }
}

.c-content__toggle {
  appearance: none;
  background: transparent none;
  border: 0;
  border-radius: 0;
  display: table;
  display: flex;
  align-items: flex-start;
  width: max-content;
  font-family: settings.$font-secondary;
  @include font.size(16px, 1);
  margin: rem.convert(16px) 0 0 auto;
  padding: rem.convert(4px) 0;
  right: 0;
  transition: color 400ms;

  & svg {
    transition: fill 400ms;
    fill: black;
  }

  &:hover {
    color: settings.$color-primary;

    svg {
      fill: settings.$color-primary;
    }
  }
}
</style>

<style scoped lang="scss">
/* code block syntax highlighting inside README */
.c-content ::v-deep() {
  /* generated with pygments */
  .highlight .hll { background-color: #ffffcc }
  .highlight  { background: #f8f8f8; }
  .highlight .c { color: #408080; font-style: italic } /* Comment */
  .highlight .err { border: 1px solid #FF0000 } /* Error */
  .highlight .k { color: #008000; font-weight: bold } /* Keyword */
  .highlight .o { color: #666666 } /* Operator */
  .highlight .ch { color: #408080; font-style: italic } /* Comment.Hashbang */
  .highlight .cm { color: #408080; font-style: italic } /* Comment.Multiline */
  .highlight .cp { color: #BC7A00 } /* Comment.Preproc */
  .highlight .cpf { color: #408080; font-style: italic } /* Comment.PreprocFile */
  .highlight .c1 { color: #408080; font-style: italic } /* Comment.Single */
  .highlight .cs { color: #408080; font-style: italic } /* Comment.Special */
  .highlight .gd { color: #A00000 } /* Generic.Deleted */
  .highlight .ge { font-style: italic } /* Generic.Emph */
  .highlight .gr { color: #FF0000 } /* Generic.Error */
  .highlight .gh { color: #000080; font-weight: bold } /* Generic.Heading */
  .highlight .gi { color: #00A000 } /* Generic.Inserted */
  .highlight .go { color: #888888 } /* Generic.Output */
  .highlight .gp { color: #000080; font-weight: bold } /* Generic.Prompt */
  .highlight .gs { font-weight: bold } /* Generic.Strong */
  .highlight .gu { color: #800080; font-weight: bold } /* Generic.Subheading */
  .highlight .gt { color: #0044DD } /* Generic.Traceback */
  .highlight .kc { color: #008000; font-weight: bold } /* Keyword.Constant */
  .highlight .kd { color: #008000; font-weight: bold } /* Keyword.Declaration */
  .highlight .kn { color: #008000; font-weight: bold } /* Keyword.Namespace */
  .highlight .kp { color: #008000 } /* Keyword.Pseudo */
  .highlight .kr { color: #008000; font-weight: bold } /* Keyword.Reserved */
  .highlight .kt { color: #B00040 } /* Keyword.Type */
  .highlight .m { color: #666666 } /* Literal.Number */
  .highlight .s { color: #BA2121 } /* Literal.String */
  .highlight .na { color: #7D9029 } /* Name.Attribute */
  .highlight .nb { color: #008000 } /* Name.Builtin */
  .highlight .nc { color: #0000FF; font-weight: bold } /* Name.Class */
  .highlight .no { color: #880000 } /* Name.Constant */
  .highlight .nd { color: #AA22FF } /* Name.Decorator */
  .highlight .ni { color: #999999; font-weight: bold } /* Name.Entity */
  .highlight .ne { color: #D2413A; font-weight: bold } /* Name.Exception */
  .highlight .nf { color: #0000FF } /* Name.Function */
  .highlight .nl { color: #A0A000 } /* Name.Label */
  .highlight .nn { color: #0000FF; font-weight: bold } /* Name.Namespace */
  .highlight .nt { color: #008000; font-weight: bold } /* Name.Tag */
  .highlight .nv { color: #19177C } /* Name.Variable */
  .highlight .ow { color: #AA22FF; font-weight: bold } /* Operator.Word */
  .highlight .w { color: #bbbbbb } /* Text.Whitespace */
  .highlight .mb { color: #666666 } /* Literal.Number.Bin */
  .highlight .mf { color: #666666 } /* Literal.Number.Float */
  .highlight .mh { color: #666666 } /* Literal.Number.Hex */
  .highlight .mi { color: #666666 } /* Literal.Number.Integer */
  .highlight .mo { color: #666666 } /* Literal.Number.Oct */
  .highlight .sa { color: #BA2121 } /* Literal.String.Affix */
  .highlight .sb { color: #BA2121 } /* Literal.String.Backtick */
  .highlight .sc { color: #BA2121 } /* Literal.String.Char */
  .highlight .dl { color: #BA2121 } /* Literal.String.Delimiter */
  .highlight .sd { color: #BA2121; font-style: italic } /* Literal.String.Doc */
  .highlight .s2 { color: #BA2121 } /* Literal.String.Double */
  .highlight .se { color: #BB6622; font-weight: bold } /* Literal.String.Escape */
  .highlight .sh { color: #BA2121 } /* Literal.String.Heredoc */
  .highlight .si { color: #BB6688; font-weight: bold } /* Literal.String.Interpol */
  .highlight .sx { color: #008000 } /* Literal.String.Other */
  .highlight .sr { color: #BB6688 } /* Literal.String.Regex */
  .highlight .s1 { color: #BA2121 } /* Literal.String.Single */
  .highlight .ss { color: #19177C } /* Literal.String.Symbol */
  .highlight .bp { color: #008000 } /* Name.Builtin.Pseudo */
  .highlight .fm { color: #0000FF } /* Name.Function.Magic */
  .highlight .vc { color: #19177C } /* Name.Variable.Class */
  .highlight .vg { color: #19177C } /* Name.Variable.Global */
  .highlight .vi { color: #19177C } /* Name.Variable.Instance */
  .highlight .vm { color: #19177C } /* Name.Variable.Magic */
  .highlight .il { color: #666666 } /* Literal.Number.Integer.Long */
}
</style>
